/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * Date Validator - Phase 9 Migration
 * Валидация дат и диапазонов дат
 */


/**
 * Date Validator Class
 * Управляет валидацией дат и диапазонов
 */
class DateValidator {
    constructor() {
        this.initialized = false;
        this.validationRules = new Map();
        this.minYear = 1900;
        this.maxYear = 2100;
        
        this.initializeValidationRules();
        
        // Manager initialization logging only in debug mode
    }
    
    /**
     * Инициализация правил валидации
     */
    initializeValidationRules() {
        // Basic date validation
        this.validationRules.set('basic', {
            minYear: this.minYear,
            maxYear: this.maxYear,
            allowFuture: true,
            allowPast: true
        });
        
        // Past dates only
        this.validationRules.set('past-only', {
            minYear: this.minYear,
            maxYear: new Date().getFullYear(),
            allowFuture: false,
            allowPast: true,
            maxDate: new Date()
        });
        
        // Future dates only
        this.validationRules.set('future-only', {
            minYear: new Date().getFullYear(),
            maxYear: this.maxYear,
            allowFuture: true,
            allowPast: false,
            minDate: new Date()
        });
        
        // Birth date validation
        this.validationRules.set('birth-date', {
            minYear: 1900,
            maxYear: new Date().getFullYear(),
            allowFuture: false,
            allowPast: true,
            maxDate: new Date(),
            maxAge: 150
        });
        
        // Event date validation
        this.validationRules.set('event-date', {
            minYear: new Date().getFullYear() - 50,
            maxYear: new Date().getFullYear() + 10,
            allowFuture: true,
            allowPast: true
        });
    }
    
    /**
     * Основная функция валидации даты
     * Мигрировано из legacy validateDate
     */
    validateDate(date, rules = 'basic') {
        if (!date) {
            return {
                isValid: false,
                error: 'Date is required'
            };
        }
        
        // Convert to Date object if needed
        let dateObj = date;
        if (typeof date === 'string') {
            dateObj = new Date(date);
        }
        
        if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
            return {
                isValid: false,
                error: 'Invalid date format'
            };
        }
        
        try {
            const validationRule = this.getValidationRule(rules);
            return this.validateWithRule(dateObj, validationRule);
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[DateValidator] Validation error:', error);
            }
            return {
                isValid: false,
                error: 'Validation failed'
            };
        }
    }
    
    /**
     * Валидация диапазона дат
     * Мигрировано из legacy validateDateRange
     */
    validateDateRange(startDate, endDate, rules = 'basic') {
        if (!startDate || !endDate) {
            return {
                isValid: false,
                error: 'Both start and end dates are required'
            };
        }
        
        // Convert to Date objects if needed
        let startDateObj = startDate;
        let endDateObj = endDate;
        
        if (typeof startDate === 'string') {
            startDateObj = new Date(startDate);
        }
        if (typeof endDate === 'string') {
            endDateObj = new Date(endDate);
        }
        
        // Validate individual dates first
        const startValidation = this.validateDate(startDateObj, rules);
        if (!startValidation.isValid) {
            return {
                isValid: false,
                error: `Start date error: ${startValidation.error}`
            };
        }
        
        const endValidation = this.validateDate(endDateObj, rules);
        if (!endValidation.isValid) {
            return {
                isValid: false,
                error: `End date error: ${endValidation.error}`
            };
        }
        
        // Validate range
        if (startDateObj.getTime() >= endDateObj.getTime()) {
            return {
                isValid: false,
                error: 'Start date must be before end date'
            };
        }
        
        // Check range duration if specified
        const validationRule = this.getValidationRule(rules);
        if (validationRule.maxRangeDays) {
            const diffInDays = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
            if (diffInDays > validationRule.maxRangeDays) {
                return {
                    isValid: false,
                    error: `Date range cannot exceed ${validationRule.maxRangeDays} days`
                };
            }
        }
        
        if (window.HyperLog) {
            window.HyperLog.debug('[DateValidator] Date range validated:', {
                start: startDateObj.toISOString(),
                end: endDateObj.toISOString(),
                isValid: true
            });
        }
        
        return {
            isValid: true,
            startDate: startDateObj,
            endDate: endDateObj,
            duration: Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24))
        };
    }
    
    /**
     * Валидация с конкретным правилом
     */
    validateWithRule(date, rule) {
        const year = date.getFullYear();
        const now = new Date();
        
        // Year range validation
        if (year < rule.minYear || year > rule.maxYear) {
            return {
                isValid: false,
                error: `Year must be between ${rule.minYear} and ${rule.maxYear}`
            };
        }
        
        // Past/Future validation
        if (!rule.allowPast && date < now) {
            return {
                isValid: false,
                error: 'Past dates are not allowed'
            };
        }
        
        if (!rule.allowFuture && date > now) {
            return {
                isValid: false,
                error: 'Future dates are not allowed'
            };
        }
        
        // Min/Max date validation
        if (rule.minDate && date < rule.minDate) {
            return {
                isValid: false,
                error: 'Date is before minimum allowed date'
            };
        }
        
        if (rule.maxDate && date > rule.maxDate) {
            return {
                isValid: false,
                error: 'Date is after maximum allowed date'
            };
        }
        
        // Age validation (for birth dates)
        if (rule.maxAge) {
            const ageInYears = (now - date) / (1000 * 60 * 60 * 24 * 365.25);
            if (ageInYears > rule.maxAge) {
                return {
                    isValid: false,
                    error: `Age cannot exceed ${rule.maxAge} years`
                };
            }
        }
        
        return {
            isValid: true,
            date: date
        };
    }
    
    /**
     * Получение правила валидации
     */
    getValidationRule(rules) {
        if (typeof rules === 'string') {
            return this.validationRules.get(rules) || this.validationRules.get('basic');
        }
        
        if (typeof rules === 'object') {
            return { ...this.validationRules.get('basic'), ...rules };
        }
        
        return this.validationRules.get('basic');
    }
    
    /**
     * Валидация формата даты
     */
    validateDateFormat(dateString, expectedFormat) {
        if (!dateString || typeof dateString !== 'string') {
            return {
                isValid: false,
                error: 'Date string is required'
            };
        }
        
        const formatPatterns = {
            'YYYY-MM-DD': /^\d{4}-\d{2}-\d{2}$/,
            'DD.MM.YYYY': /^\d{2}\.\d{2}\.\d{4}$/,
            'MM/DD/YYYY': /^\d{2}\/\d{2}\/\d{4}$/,
            'DD/MM/YYYY': /^\d{2}\/\d{2}\/\d{4}$/,
            'YYYY/MM/DD': /^\d{4}\/\d{2}\/\d{2}$/,
            'ISO8601': /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
        };
        
        const pattern = formatPatterns[expectedFormat];
        if (!pattern) {
            return {
                isValid: false,
                error: `Unknown format: ${expectedFormat}`
            };
        }
        
        if (!pattern.test(dateString)) {
            return {
                isValid: false,
                error: `Date does not match expected format: ${expectedFormat}`
            };
        }
        
        return {
            isValid: true,
            format: expectedFormat
        };
    }
    
    /**
     * Проверка високосного года
     */
    isLeapYear(year) {
        return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    }
    
    /**
     * Получение количества дней в месяце
     */
    getDaysInMonth(month, year) {
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        
        if (month === 2 && this.isLeapYear(year)) {
            return 29;
        }
        
        return daysInMonth[month - 1] || 0;
    }
    
    /**
     * Валидация дня месяца
     */
    validateDayInMonth(day, month, year) {
        const maxDays = this.getDaysInMonth(month, year);
        return day >= 1 && day <= maxDays;
    }
    
    /**
     * Получение диапазона дат
     * Мигрировано из legacy getDateRange
     */
    getDateRange(startDate, endDate) {
        if (!startDate || !endDate) {
            return null;
        }
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            return null;
        }
        
        const diffInMs = end.getTime() - start.getTime();
        const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
        const diffInWeeks = Math.ceil(diffInDays / 7);
        const diffInMonths = Math.ceil(diffInDays / 30.44); // Average days per month
        
        return {
            startDate: start,
            endDate: end,
            duration: {
                milliseconds: diffInMs,
                days: diffInDays,
                weeks: diffInWeeks,
                months: diffInMonths,
                years: Math.floor(diffInMonths / 12)
            },
            isValid: diffInMs > 0
        };
    }
    
    /**
     * Валидация возраста
     */
    validateAge(birthDate, minAge = 0, maxAge = 150) {
        if (!birthDate) {
            return {
                isValid: false,
                error: 'Birth date is required'
            };
        }
        
        const birth = new Date(birthDate);
        if (isNaN(birth.getTime())) {
            return {
                isValid: false,
                error: 'Invalid birth date'
            };
        }
        
        const now = new Date();
        const ageInMs = now.getTime() - birth.getTime();
        const ageInYears = ageInMs / (1000 * 60 * 60 * 24 * 365.25);
        
        if (ageInYears < minAge) {
            return {
                isValid: false,
                error: `Age must be at least ${minAge} years`
            };
        }
        
        if (ageInYears > maxAge) {
            return {
                isValid: false,
                error: `Age cannot exceed ${maxAge} years`
            };
        }
        
        return {
            isValid: true,
            age: Math.floor(ageInYears),
            exactAge: ageInYears
        };
    }
    
    /**
     * Валидация рабочих дней
     */
    validateBusinessDays(startDate, endDate, excludeWeekends = true, excludeHolidays = []) {
        const range = this.getDateRange(startDate, endDate);
        if (!range || !range.isValid) {
            return {
                isValid: false,
                error: 'Invalid date range'
            };
        }
        
        let businessDays = 0;
        let currentDate = new Date(range.startDate);
        
        while (currentDate <= range.endDate) {
            const dayOfWeek = currentDate.getDay();
            const dateString = currentDate.toISOString().split('T')[0];
            
            // Check if it's a weekend
            const isWeekend = excludeWeekends && (dayOfWeek === 0 || dayOfWeek === 6);
            
            // Check if it's a holiday
            const isHoliday = excludeHolidays.includes(dateString);
            
            if (!isWeekend && !isHoliday) {
                businessDays++;
            }
            
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        return {
            isValid: true,
            businessDays,
            totalDays: range.duration.days,
            weekends: range.duration.days - businessDays - excludeHolidays.length,
            holidays: excludeHolidays.length
        };
    }
    
    /**
     * Добавление пользовательского правила валидации
     */
    addValidationRule(name, rule) {
        this.validationRules.set(name, rule);
        if (window.HyperLog) {
            window.HyperLog.debug('[DateValidator] Custom validation rule added:', name);
        }
    }
    
    /**
     * Получение всех правил валидации
     */
    getValidationRules() {
        const rules = {};
        for (const [key, value] of this.validationRules) {
            rules[key] = { ...value };
        }
        return rules;
    }
    
    /**
     * Получение статистики валидатора
     */
    getValidatorStats() {
        return {
            initialized: this.initialized,
            validationRules: this.validationRules.size,
            minYear: this.minYear,
            maxYear: this.maxYear
        };
    }
}

// Создаем singleton instance
const dateValidator = new DateValidator();

// Global exports для backward compatibility
window.dateValidator = dateValidator;
window.validateDate = dateValidator.validateDate.bind(dateValidator);
window.validateDateRange = dateValidator.validateDateRange.bind(dateValidator);
window.getDateRange = dateValidator.getDateRange.bind(dateValidator);

// ES Module exports
export { dateValidator };
export const { validateDate, validateDateRange, getDateRange } = dateValidator; 